/**
 * SnapLog 시나리오 정의
 * 각 시나리오는 capture → analyze → record → output 파이프라인 설정을 포함
 */

export const CATEGORIES = {
  learning: { label: '학습', icon: '📚', color: '#667eea' },
  inspection: { label: '설비점검', icon: '🔧', color: '#e67e22' },
  health: { label: '건강/운동', icon: '💪', color: '#e91e63' },
};

export const SCENARIOS = {
  // === 학습 시나리오 ===
  math_study: {
    id: 'math_study',
    name: '수학 오답노트',
    description: '수학 문제를 촬영하면 풀이 과정과 오답 분석을 제공합니다',
    category: 'learning',
    icon: '🔢',
    color: '#667eea',
    capture: {
      title: '수학 문제 촬영',
      instruction: '틀린 문제를 촬영해주세요',
      acceptMultiple: true,
      maxImages: 5,
      questionPlaceholder: '궁금한 점이 있으면 입력해주세요 (선택)',
    },
    analyzers: [
      {
        id: 'problem_analysis',
        name: '문제 분석',
        prompt: `이 수학 문제 이미지를 분석해주세요.

다음 JSON 형식으로 응답해주세요:
{
  "problem_text": "문제 내용 (이미지에서 추출한 정확한 텍스트, 수식 포함)",
  "student_answer": "학생이 작성한 답 (있다면)",
  "correct_answer": "정답",
  "explanation": "상세한 해설 (단계별로 설명)",
  "key_concept": "핵심 개념 (1-2문장)",
  "difficulty": "난이도 (쉬움/보통/어려움)",
  "confidence": "분석 신뢰도 (0.0~1.0 사이 소수, 예: 0.85)",
  "similar_problems": "유사 문제 유형 설명 (1-2문장)",
  "common_mistakes": "자주 하는 실수 (1-2문장)"
}

중요:
- 수식을 정확히 추출하고 풀이 과정을 단계별로 설명해주세요.
- 학생 답이 없으면 student_answer는 빈 문자열로 하세요.
- confidence는 분석 정확도에 대한 자기 평가입니다.`,
      }
    ],
    record: {
      level: '자동 감지',
      subject: '수학',
      defaultTags: ['수학', '오답노트'],
    },
    outputs: ['pdf', 'share'],
  },

  study_sheet: {
    id: 'study_sheet',
    name: '학습 시트 분석',
    description: '학습지나 시험지를 촬영하면 문제와 답을 분석합니다',
    category: 'learning',
    icon: '📝',
    color: '#764ba2',
    capture: {
      title: '학습지 촬영',
      instruction: '학습지 또는 시험지를 촬영해주세요',
      acceptMultiple: true,
      maxImages: 10,
      questionPlaceholder: '분석해 줄 내용을 입력해주세요 (선택)',
    },
    analyzers: [
      {
        id: 'sheet_analysis',
        name: '학습지 분석',
        prompt: `이 학습지/시험지 이미지를 분석해주세요.

다음 JSON 형식으로 응답해주세요:
{
  "detected_level": "추출된 학습 레벨 (중학생/고등학생/대학생/일반인)",
  "detected_subject": "추출된 과목",
  "problem_text": "문제 내용 (모든 문제를 텍스트로 추출)",
  "student_answer": "학생이 작성한 답 (있다면)",
  "correct_answer": "정답 (가능하면)",
  "explanation": "전체적인 분석 및 해설",
  "key_concept": "핵심 개념들",
  "difficulty": "전체 난이도 (쉬움/보통/어려움)",
  "confidence": "분석 신뢰도 (0.0~1.0 사이 소수, 예: 0.85)",
  "total_questions": "총 문제 수",
  "summary": "학습지 요약 (1-2문장)"
}

중요:
- 여러 문제가 있으면 모두 추출해주세요.
- 과목과 레벨을 자동으로 감지해주세요.
- confidence는 분석 정확도에 대한 자기 평가입니다.`,
      }
    ],
    record: {
      level: '자동 감지',
      subject: '자동 감지',
      defaultTags: ['학습지'],
    },
    outputs: ['pdf', 'share'],
  },

  // === 설비점검 시나리오 ===
  hvac_inspection: {
    id: 'hvac_inspection',
    name: 'HVAC 설비점검',
    description: '공조설비 사진을 촬영하면 상태를 분석하고 점검 보고서를 생성합니다',
    category: 'inspection',
    icon: '❄️',
    color: '#2ecc71',
    capture: {
      title: 'HVAC 설비 촬영',
      instruction: '공조설비(에어컨, 실외기, 덕트 등)를 촬영해주세요',
      acceptMultiple: true,
      maxImages: 10,
      questionPlaceholder: '특이사항이 있으면 입력해주세요 (선택)',
    },
    analyzers: [
      {
        id: 'hvac_analysis',
        name: 'HVAC 상태 분석',
        prompt: `이 HVAC(공조설비) 이미지를 분석해주세요.

다음 JSON 형식으로 응답해주세요:
{
  "equipment_type": "설비 종류 (에어컨/실외기/AHU/덕트/배관 등)",
  "manufacturer": "제조사 (판독 가능 시)",
  "model": "모델명 (판독 가능 시)",
  "condition": "상태 (양호/주의/불량/위험)",
  "condition_score": "상태 점수 (1-10, 10이 최상)",
  "confidence": "분석 신뢰도 (0.0~1.0 사이 소수)",
  "issues_found": [{"item": "문제점", "urgency": "즉시/단기/장기"}],
  "recommendations": [{"item": "조치사항", "urgency": "즉시/단기/장기"}],
  "checklist": [
    {"item": "외관 상태", "status": "양호/주의/불량", "note": "비고"},
    {"item": "배관 상태", "status": "양호/주의/불량", "note": "비고"},
    {"item": "전기 배선", "status": "양호/주의/불량", "note": "비고"},
    {"item": "소음/진동", "status": "양호/주의/불량", "note": "비고"},
    {"item": "냉매 누출", "status": "양호/주의/불량", "note": "비고"}
  ],
  "next_inspection": "다음 점검 권장 시기",
  "safety_concerns": "안전 관련 우려사항 (있을 경우)",
  "description": "전체적인 설비 상태 설명"
}

중요:
- 녹, 부식, 누수, 진동 흔적, 비정상 배선 등을 꼼꼼히 확인해주세요.
- 안전 관련 문제가 있으면 반드시 지적해주세요.
- issues_found와 recommendations에 urgency(긴급도)를 반드시 포함해주세요.
- checklist는 점검 항목별 상태를 표시합니다.
- confidence는 분석 정확도에 대한 자기 평가입니다.
- 판독 불가한 항목은 빈 문자열로 하세요.`,
      }
    ],
    record: {
      level: '',
      subject: 'HVAC',
      defaultTags: ['HVAC', '설비점검'],
    },
    outputs: ['pdf', 'share'],
  },

  fire_safety: {
    id: 'fire_safety',
    name: '소방 안전점검',
    description: '소방시설 사진을 촬영하면 적합 여부를 분석합니다',
    category: 'inspection',
    icon: '🧯',
    color: '#e74c3c',
    capture: {
      title: '소방시설 촬영',
      instruction: '소방시설(소화기, 감지기, 스프링클러 등)을 촬영해주세요',
      acceptMultiple: true,
      maxImages: 10,
      questionPlaceholder: '특이사항이 있으면 입력해주세요 (선택)',
    },
    analyzers: [
      {
        id: 'fire_safety_analysis',
        name: '소방 안전 분석',
        prompt: `이 소방시설 이미지를 분석해주세요.

다음 JSON 형식으로 응답해주세요:
{
  "equipment_type": "시설 종류 (소화기/감지기/스프링클러/유도등/소화전 등)",
  "manufacturer": "제조사 (판독 가능 시)",
  "expiry_date": "유효기간 (판독 가능 시)",
  "condition": "상태 (양호/주의/불량/위험)",
  "condition_score": "상태 점수 (1-10, 10이 최상)",
  "confidence": "분석 신뢰도 (0.0~1.0 사이 소수)",
  "compliance": "법규 적합 여부 (적합/부적합/확인필요)",
  "issues_found": [{"item": "문제점", "urgency": "즉시/단기/장기"}],
  "recommendations": [{"item": "조치사항", "urgency": "즉시/단기/장기"}],
  "checklist": [
    {"item": "외관 상태", "status": "양호/주의/불량", "note": "비고"},
    {"item": "유효기간", "status": "양호/주의/불량", "note": "비고"},
    {"item": "압력게이지", "status": "양호/주의/불량", "note": "비고"},
    {"item": "안전핀/봉인", "status": "양호/주의/불량", "note": "비고"},
    {"item": "설치 위치", "status": "양호/주의/불량", "note": "비고"}
  ],
  "safety_concerns": "안전 관련 우려사항",
  "description": "전체적인 시설 상태 설명"
}

중요:
- 유효기간, 압력게이지, 파손, 부식 등을 확인해주세요.
- 소방법규 기준에 따라 적합 여부를 판단해주세요.
- issues_found와 recommendations에 urgency(긴급도)를 반드시 포함해주세요.
- checklist는 점검 항목별 상태를 표시합니다.
- confidence는 분석 정확도에 대한 자기 평가입니다.
- 즉시 교체/수리가 필요한 경우 반드시 지적해주세요.`,
      }
    ],
    record: {
      level: '',
      subject: '소방',
      defaultTags: ['소방', '안전점검'],
    },
    outputs: ['pdf', 'share'],
  },

  // === 건강/운동 시나리오 ===
  exercise_coaching: {
    id: 'exercise_coaching',
    name: '운동 자세 교정',
    description: '운동 자세를 촬영하면 AI가 자세를 분석하고 교정 방법을 알려줍니다',
    category: 'health',
    icon: '🏋️',
    color: '#e91e63',
    capture: {
      title: '운동 자세 촬영',
      instruction: '운동하는 모습을 촬영해주세요 (전신이 보이면 좋습니다)',
      acceptMultiple: true,
      maxImages: 5,
      questionPlaceholder: '어떤 운동인지 알려주세요 (선택)',
    },
    analyzers: [
      {
        id: 'exercise_analysis',
        name: '자세 분석',
        prompt: `이 운동 자세 이미지를 분석해주세요.

다음 JSON 형식으로 응답해주세요:
{
  "exercise_type": "운동 종류 (스쿼트/데드리프트/플랭크/러닝 등)",
  "posture_score": "자세 점수 (1-10, 10이 최상)",
  "confidence": "분석 신뢰도 (0.0~1.0)",
  "good_points": ["잘하고 있는 점 목록"],
  "issues_found": ["자세 문제점 목록"],
  "corrections": ["교정 방법 목록 (구체적으로)"],
  "injury_risk": "부상 위험도 (낮음/보통/높음)",
  "recommendations": ["추가 권장사항"],
  "description": "전체적인 자세 평가"
}

중요:
- 관절 각도, 척추 정렬, 무게 중심 등을 분석해주세요.
- 부상 위험이 있는 자세는 반드시 지적해주세요.
- 교정 방법은 구체적으로 설명해주세요.`,
      }
    ],
    record: {
      level: '',
      subject: '운동',
      defaultTags: ['운동', '자세교정'],
    },
    outputs: ['pdf', 'share'],
  },

  health_checkup: {
    id: 'health_checkup',
    name: '건강검진 결과 분석',
    description: '건강검진 결과지를 촬영하면 AI가 수치를 분석하고 해설합니다',
    category: 'health',
    icon: '🏥',
    color: '#00bcd4',
    capture: {
      title: '검진 결과 촬영',
      instruction: '건강검진 결과지를 촬영해주세요',
      acceptMultiple: true,
      maxImages: 10,
      questionPlaceholder: '특별히 궁금한 항목이 있으면 입력해주세요 (선택)',
    },
    analyzers: [
      {
        id: 'health_analysis',
        name: '검진 결과 분석',
        prompt: `이 건강검진 결과지 이미지를 분석해주세요.

다음 JSON 형식으로 응답해주세요:
{
  "checkup_type": "검진 유형 (일반/종합/국가건강검진 등)",
  "overall_status": "종합 판정 (정상/경계/이상)",
  "confidence": "분석 신뢰도 (0.0~1.0)",
  "abnormal_items": [{"item": "항목명", "value": "수치", "reference": "참고치", "status": "정상/경계/이상", "explanation": "해설"}],
  "normal_items": [{"item": "항목명", "value": "수치", "status": "정상"}],
  "lifestyle_advice": ["생활 습관 조언 목록"],
  "follow_up": ["추가 검사 필요 항목"],
  "description": "전체적인 건강 상태 요약"
}

중요:
- 이것은 AI 참고용 분석이며, 정확한 진단은 의사에게 문의해야 합니다.
- 이상 수치가 있으면 해당 항목의 의미와 주의사항을 설명해주세요.
- 참고치와 비교하여 설명해주세요.`,
      }
    ],
    record: {
      level: '',
      subject: '건강',
      defaultTags: ['건강검진', '건강'],
    },
    outputs: ['pdf', 'share'],
  },
};

/**
 * 카테고리별 시나리오 목록 반환
 */
export function getScenariosByCategory(category) {
  return Object.values(SCENARIOS).filter(s => s.category === category);
}

/**
 * 시나리오 ID로 조회
 */
export function getScenarioById(id) {
  return SCENARIOS[id] || null;
}

/**
 * 전체 시나리오 목록
 */
export function getAllScenarios() {
  return Object.values(SCENARIOS);
}
